import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import type { ErrorComponentProps } from "@tanstack/react-router";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { useEffect } from "react";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { WhatsAppButton } from "@/components/ui/whatsapp-button";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import appCss from "../styles.css?url";
import { consentBootstrapScript } from "@/lib/consent";
import { ConsentManager } from "@/components/consent/ConsentManager";
import { AnalyticsTracker } from "@/components/analytics/AnalyticsTracker";
import { ORGANIZATION_ID, WEBSITE_ID, organizationSchema } from "@/lib/site-schema";

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap";

const webSiteSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: "SEOEVOLUTION",
  url: "https://agenciaseoevolution.com/",
  inLanguage: "es-ES",
  publisher: { "@id": ORGANIZATION_ID },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: "https://agenciaseoevolution.com/blog?q={search_term_string}",
    },
    "query-input": "required name=search_term_string",
  },
});


export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      {
        title:
          "SEOEVOLUTION | Agencia SEO Premium con IA en España",
      },
      {
        name: "description",
        content:
          "Agencia SEO con Inteligencia Artificial y exclusividad por sector y zona. SEO local, web en React y ecommerce. Compromiso mínimo: SEO local 3 meses; GEO 6 meses. Resultados desde el mes 1.",
      },
      { name: "author", content: "SEOEVOLUTION" },
      {
        name: "keywords",
        content:
          "SEO, IA, posicionamiento web, marketing digital, agencia SEO, inteligencia artificial",
      },
      { name: "msvalidate.01", content: "7A974A535089F28CE182015C3C52F128" },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://agenciaseoevolution.com/" },
      {
        property: "og:title",
        content:
          "SEOEVOLUTION | Agencia SEO Premium con IA en España",
      },
      {
        property: "og:description",
        content:
          "Agencia SEO con Inteligencia Artificial y exclusividad por sector y zona. SEO local, web en React y ecommerce. Compromiso mínimo: SEO local 3 meses; GEO 6 meses. Resultados desde el mes 1.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@seoevolution" },
      {
        name: "twitter:title",
        content:
          "SEOEVOLUTION | Agencia SEO Premium con IA en España",
      },
      {
        name: "twitter:description",
        content:
          "Agencia SEO con Inteligencia Artificial y exclusividad por sector y zona. SEO local, web en React y ecommerce. Compromiso mínimo: SEO local 3 meses; GEO 6 meses. Resultados desde el mes 1.",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: FONTS_HREF },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
    ],
    // Unique ids so HeadContent never silently dedupes one inline script into another.
    scripts: [
      { id: "seoevo-schema-website", type: "application/ld+json", children: webSiteSchema },
      { id: "seoevo-schema-organization", type: "application/ld+json", children: JSON.stringify(organizationSchema) },
      // Consent Mode v2 defaults (denied) before any tag. GTM is injected by ConsentManager only with consent.
      { id: "seoevo-consent-bootstrap", children: consentBootstrapScript },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="es" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ScrollToTop />
        <Outlet />
        <WhatsAppButton />
        <ConsentManager />
        <AnalyticsTracker />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  useEffect(() => {
    console.error(error);
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground px-4">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-2xl font-bold">This page didn&apos;t load</h1>
        <p className="text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="flex gap-3 justify-center flex-wrap">
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a href="/" className="px-4 py-2 rounded-md border border-border">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
